import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUBJECT_NAMES } from '@padcanvas/shared';
import { SearchFilterBar, type FilterDef } from '../components';
import type { ChapterExtra, SimulationListing, SimulationsSource } from './types';
import { ChapterListView, type ChapterListViewModel } from '../chapter-list';
import './simulations.css';

const label = (s: string) => SUBJECT_NAMES[s] ?? s;

/** The same two keys the gallery uses, so a search typed in one tab still
 *  means something in the other. Everything else in the URL stays put. */
const FILTER_KEYS = ['search', 'subject'] as const;

interface Chapter {
  id: string;
  name: string;
  subject: string;
  sims: SimulationListing[];
  extras: ChapterExtra[];
}

/** The chapter a simulation has no chapter for: one bucket, at the end. */
const UNPLACED = '__unplaced__';

interface Props<T extends SimulationListing> {
  source: SimulationsSource<T>;
  basePath?: string;
  /** Benches, which belong to a chapter but are not in the catalogue. */
  extras?: ChapterExtra[];
}

/**
 * The catalogue cut by chapter, as zitmus shows it.
 *
 * A chapter is not a page of its own — it is a way of finding a simulation, so
 * each row lists what it contains and every one of those is the link. A teacher
 * revising a chapter tonight starts here rather than in the full catalogue.
 */
export function SimulationChapters<T extends SimulationListing>({ source, basePath = '/simulations', extras = [] }: Props<T>) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const subject = searchParams.get('subject') ?? '';
  const [sims, setSims] = useState<T[]>([]);
  const [modules, setModules] = useState<{ id: string; displayName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setFilters = (next: Record<string, string>) =>
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      for (const k of FILTER_KEYS) { const v = next[k]; if (v) p.set(k, v); else p.delete(k); }
      return p;
    }, { replace: true });

  useEffect(() => {
    source
      .list()
      .then(setSims)
      .catch((e) => setError(e.message || 'Failed to load simulations'))
      .finally(() => setLoading(false));
  }, [source]);

  useEffect(() => {
    source.moduleNames().then(setModules).catch(() => setModules([]));
  }, [source]);

  const chapters = useMemo<Chapter[]>(() => {
    const byId = new Map(modules.map((m) => [m.id, m.displayName]));
    const out = new Map<string, Chapter>();
    for (const sim of sims) {
      // A simulation in three chapters belongs to all three — it is filed
      // under each, not split between them.
      const ids = sim.moduleIds?.length ? sim.moduleIds : [UNPLACED];
      for (const id of ids) {
        const name = id === UNPLACED ? 'Not filed under a chapter' : byId.get(id) ?? id;
        const row = out.get(id) ?? { id, name, subject: sim.subject, sims: [], extras: [] };
        row.sims.push(sim);
        out.set(id, row);
      }
    }
    // A bench joins the chapters it names — but only chapters that exist here:
    // a row of its own, holding nothing a reader can compare it with, says
    // less than leaving it to the tab that is entirely about it.
    for (const extra of extras) {
      for (const id of extra.chapterIds) {
        const row = out.get(id);
        if (row) row.extras.push(extra);
      }
    }
    return Array.from(out.values()).sort((a, b) => {
      // The unplaced bucket is a leftover, not a chapter: it goes last.
      if ((a.id === UNPLACED) !== (b.id === UNPLACED)) return a.id === UNPLACED ? 1 : -1;
      return label(a.subject).localeCompare(label(b.subject)) || a.name.localeCompare(b.name);
    });
    // Extras too: a teacher's saved setups arrive after the catalogue does.
  }, [sims, modules, extras]);

  const filterDefs = useMemo<FilterDef[]>(() => [{
    key: 'subject',
    placeholder: 'All subjects',
    options: Array.from(new Set(sims.map((s) => s.subject))).sort().map((s) => ({ value: s, label: label(s) })),
  }], [sims]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return chapters
      .filter((c) => !subject || c.subject === subject)
      // A chapter matches on its own name OR on anything in it, so searching a
      // simulation's title finds the chapter that holds it.
      .filter((c) => !q || c.name.toLowerCase().includes(q)
        || c.sims.some((s) => s.title.toLowerCase().includes(q))
        || c.extras.some((e) => e.title.toLowerCase().includes(q)));
  }, [chapters, search, subject]);

  /** Where the catalogue's thumbnails are served from — a bench's picture sits
   *  beside them, and only a loaded simulation knows the host. */
  const thumbBase = useMemo(
    () => sims.find((s) => s.thumbnailUrl)?.thumbnailUrl?.replace(/[^/]+$/, '') ?? '',
    [sims],
  );

  /**
   * BuzzBrain's view model.
   *
   * This is the half that differs from zitmus: the catalogue arrives over an
   * API rather than at compile time, and a chapter's entries open by routing
   * rather than by href. Everything below the view model — the cards, the
   * pairing, the thumbnails — is the shared view's, and neither product draws
   * it itself any more.
   */
  const viewModel = useMemo<ChapterListViewModel>(() => ({
    chapters: shown.map((c) => ({
      id: c.id,
      name: c.name,
      subject: c.subject,
      // BuzzBrain's wording: the subject, then what is in the chapter. zitmus
      // counts guided experiments here instead, which is why the view is
      // handed the sentence rather than the parts.
      meta: [
        label(c.subject),
        `${c.sims.length} ${c.sims.length === 1 ? 'simulation' : 'simulations'}`,
        ...(() => {
          const benches = c.extras.filter((e) => !e.mine).length;
          const mine = c.extras.length - benches;
          return [
            benches > 0 ? `${benches} ${benches === 1 ? 'bench' : 'benches'}` : '',
            mine > 0 ? `${mine} saved by you` : '',
          ];
        })(),
      ].filter(Boolean).join(' · '),
      entries: [
        ...c.sims.map((sim) => ({
          id: sim.id,
          title: sim.title,
          thumbnail: sim.thumbnailUrl
            ? { kind: 'image' as const, url: sim.thumbnailUrl }
            : { kind: 'glyph' as const, glyph: sim.glyph ?? '' },
          // An app navigates. zitmus passes `href` instead, and the view
          // renders a real anchor for it.
          onOpen: () => navigate(`${basePath}/${sim.id}`),
        })),
        ...c.extras.map((e) => ({
          id: e.id,
          title: e.title,
          thumbnail: thumbBase && e.thumbFile
            ? { kind: 'image' as const, url: `${thumbBase}${e.thumbFile}` }
            : { kind: 'lab' as const, lab: e.mark },
          kindLabel: e.tag,
          onOpen: e.open,
        })),
      ],
    })),
  }), [shown, navigate, basePath, thumbBase]);

  return (
    <div className="sim-page sim-chapters">
      <SearchFilterBar
        filters={filterDefs}
        values={{ search, subject }}
        onChange={setFilters}
        searchPlaceholder="Search a chapter or a simulation…"
        hideFetch
      />

      {loading ? (
        <div className="sim-empty">Loading chapters…</div>
      ) : error ? (
        <div className="sim-empty">{error}</div>
      ) : shown.length === 0 ? (
        <div className="sim-empty">No chapters match this search.</div>
      ) : (
        <ChapterListView chapters={viewModel.chapters} />
      )}
    </div>
  );
}
